import { Spinner } from "@amzn/awsui-components-react";
import TextContent from "@amzn/awsui-components-react/polaris/text-content";
import { useEffect, useMemo, useState } from "react";
import "./App.scss";
import { COURSE_TYPES, SB_LINK } from "./common/constants";
import { getNodeEnvironment, NodeEnvironment } from "./common/nodeEnvironment";
import { getSessionQueryParams, isValidReferrer, setSessionQueryParams } from "./common/utilityFunctions";
import { PrimaryNavigation } from "./components/PrimaryNavigation";
import { Renderer } from "./components/Renderer";
import { useAuth } from "./hooks/useAuth";
import { useGetCatalogItemById } from "./queries/catalogItemById";
import { useGetModuleCatalogItemByIdVersion } from "./queries/moduleCatalogItemByIdVersion";

const App = (): JSX.Element => {
  // TODO: SETUP LOCALIZATION
  // here we need to set query params in session memory before redirect to auth
  // this is a temporary implementatin as authgateway and gandalf currently do not support custom query params
  setSessionQueryParams();
  const logoLink = SB_LINK[getNodeEnvironment().toUpperCase() as keyof typeof NodeEnvironment];
  const [error, setError] = useState<string | undefined>(undefined);
  const [buttonLink, setButtonLink] = useState<string>(logoLink);
  const [activityId, setActivityId] = useState<string | undefined>(undefined);
  const [fileEntityId, setFileEntityId] = useState<string | undefined>(undefined);
  const [fileEntityVersion, setFileEntityVersion] = useState<string | undefined>(undefined);
  const [rendererIsLoading, setRendererIsLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const [getCatalogItemById, { data: catalogItemData, loading: catalogItemLoading, error: catalogItemError }] =
    useGetCatalogItemById();
  const [
    getModuleCatalogItemById,
    { data: moduleCatalogItemData, loading: moduleCatalogItemLoading, error: moduleCatalogItemError },
  ] = useGetModuleCatalogItemByIdVersion();

  // get the query params from session memory
  const searchParams = new URLSearchParams(getSessionQueryParams());
  const catalogProductId = searchParams.get("product_id");
  const catalogModuleId = searchParams.get("module_id");
  const navigation = searchParams.get("navigation");
  const registrationId = searchParams.get("registration_id");
  const isLcmsPreview = searchParams.get("lcms_preview") === "true";
  const isCdsPreview = searchParams.get("cds_preview") === "true";
  const lagId = searchParams.get("lagId"); // <= lagId is the learningActivityGroupId specific to the ACI courses
  const parentId = searchParams.get("parentId"); // <= parentId points to the catalogItem associated with the lagId used by ACI
  const referrer = searchParams.get("referrer"); // <= referrer is used to link back to the source that sent the user here

  useEffect(() => {
    if (!catalogModuleId) {
      // TODO: SETUP MORE ROBUST ERROR HANDLING AND PAGES IN LATER TASK
      // https://tasks.awstcprojects.com/browse/CATALOG-3965
      setError("Cannot find required query parameters.");
    }
  }, []);

  useEffect(() => {
    if (catalogProductId && user.isLoggedIn) {
      getCatalogItemById({ variables: { id: catalogProductId, isPreview: isLcmsPreview } });
    }
  }, [catalogProductId, user.isLoggedIn]);

  // Setup for preview without a product id
  useEffect(() => {
    if ((isLcmsPreview || isCdsPreview) && !catalogProductId && catalogModuleId && user.isLoggedIn) {
      getModuleCatalogItemById({ variables: { id: catalogModuleId } });
    }
  }, [catalogModuleId, user.isLoggedIn]);

  useEffect(() => {
    if (catalogItemData && catalogItemData.catalogItemByVersionedId) {
      // Valid referrer takes highest precedence
      if (referrer && isValidReferrer(referrer)) {
        setButtonLink(referrer);
      } else if (catalogItemData.catalogItemByVersionedId.trainingObjectUrl) {
        const btnUrl = new URL(catalogItemData.catalogItemByVersionedId.trainingObjectUrl);

        if (lagId) {
          btnUrl.searchParams.set("lagId", lagId);
        }
        if (parentId) {
          btnUrl.searchParams.set("parentId", parentId);
        }

        // Check if the course type is AWS Cloud Institute Digital Course
        const courseType = catalogItemData.catalogItemByVersionedId.type?.value;
        if (courseType === COURSE_TYPES.AWS_CLOUD_INSTITUTE_DIGITAL_COURSE) {
          // Add activeTab=outline parameter only for AWS Cloud Institute Digital Course
          btnUrl.searchParams.set("activeTab", "outline");
        }

        setButtonLink(btnUrl.toString());
      } else {
        setButtonLink(logoLink);
      }

      const module = catalogItemData.catalogItemByVersionedId.children.nodes.find((child) =>
        catalogModuleId?.includes(child.id)
      );
      let outline;
      if (module && module.outline) {
        outline = JSON.parse(module.outline);
      }
      if (outline && outline.length === 1) {
        setActivityId(outline[0].id);
      }
      if (module?.contentFiles && Array.isArray(module.contentFiles) && module.contentFiles.length > 0) {
        const contentFile = module.contentFiles[0];

        if (contentFile?.id && contentFile?.version) {
          setFileEntityId(contentFile.id);
          setFileEntityVersion(contentFile.version);
        } else {
          console.warn("Content file missing required properties:", contentFile);
        }
      } else {
        console.warn("No content files found in module:", module);
      }
    }
  }, [catalogItemData]);

  // Handling preview case with only module id
  useEffect(() => {
    if (
      moduleCatalogItemData &&
      moduleCatalogItemData.catalogItemByVersionedId &&
      moduleCatalogItemData.catalogItemByVersionedId.contentFiles?.length > 0
    ) {
      const contentFile = moduleCatalogItemData.catalogItemByVersionedId.contentFiles[0];
      if (contentFile?.id && contentFile?.version && contentFile?.xAPIOutline) {
        setActivityId(JSON.parse(contentFile.xAPIOutline)[0].id);
        setFileEntityId(contentFile.id);
        setFileEntityVersion(contentFile.version);
      } else {
        console.warn("Content file missing required properties:", contentFile);
      }
    } else if (moduleCatalogItemData?.catalogItemByVersionedId?.content) {
      setActivityId(JSON.parse(moduleCatalogItemData.catalogItemByVersionedId.outline)[0].id);
    }
  }, [moduleCatalogItemData]);

  //memoize to prevent unnecessary re-render
  const rendererProps = useMemo(() => {
    if (!error && user?.isLoggedIn && user?.id && user?.vibeId) {
      return {
        userId: user.id,
        vibeId: user.vibeId,
        activityId: activityId,
        catalogModuleId: searchParams.get("module_id")!,
        catalogProductId: catalogProductId,
        registrationId: registrationId,
        context: navigation,
        isLcmsPreview: isLcmsPreview,
        fileEntityId: fileEntityId,
        fileEntityVersion: fileEntityVersion,
        isLoading: rendererIsLoading,
        setIsLoading: setRendererIsLoading,
      };
    }
    return null;
  }, [
    error,
    user?.isLoggedIn,
    user?.id,
    user?.vibeId,
    activityId,
    searchParams,
    catalogProductId,
    registrationId,
    navigation,
    isLcmsPreview,
    fileEntityId,
    fileEntityVersion,
  ]);

  return (
    <>
      {navigation && catalogProductId && (
        <PrimaryNavigation
          productId={catalogProductId}
          navigationContext={navigation}
          buttonLink={buttonLink}
          logoLink={logoLink}
        />
      )}
      {error && <TextContent>Error - {error}</TextContent>}
      {!error && (!user.isLoggedIn || rendererIsLoading) && (
        <div className="spinner">
          <Spinner size="large" />
        </div>
      )}
      {rendererProps && <Renderer {...rendererProps} />}
    </>
  );
};
export default App;
